import { LinkOpenTargetEnum } from '@/enums'
import { FormItemRest } from 'ant-design-vue'

interface JSONToCSVParameters<T> {
  // 一覧のヘッダー
  header?: string
  // 一覧本体
  data: T[] | string
  // ファイル名
  fileName?: string
}

/**
 * @description JSON データを CSV ファイルに変換
 * @param data JSON を CSV に変換する為の引数
 */
export function convertJSONToCSVFile<T>({
  header,
  data,
  fileName,
}: JSONToCSVParameters<T>): void {
  let csv: string = header ?? ''

  if (typeof data === 'string') {
    csv += data
  } else {
    const converObjectToString = (item: T): string => {
      return (
        Object.keys(item as any)
          // key:value の配列を value のみの配列にする
          .map<string>((key: string) => {
            const _key = key as keyof T
            const value = item[_key] as unknown as string
            return `"${value}"`
          })
          // value のみの配列を value を `,` で区切る文字列に変更
          .join(',')
      )
    }

    csv += data.map<string>(converObjectToString).join('\n')
  }

  const link = document.createElement('a')
  // \ufeff: BOM を付ける。付けないと Excel で開ける際は文字化けになる。
  link.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent('\ufeff' + csv)
  link.target = LinkOpenTargetEnum.BLANK
  link.download = `${fileName ?? 'new-item'}.csv`
  link.click()
}

/**
 * @description JSON データを CSV ファイルに変換
 * @param data JSON を CSV に変換する為の引数
 * 使用例としては、ファイル一括アップロード画面を参照して下さい。
 */
export function convertJSONToCSVFile2<T>({
  header,
  data,
  fileName,
}: JSONToCSVParameters<T>): void {
  let csv: string = header ?? ''

  if (typeof data === 'string') {
    csv += data
  } else {
    const converObjectToString = (item: T): string => {
      return (
        Object.keys(item as any)
          // key:value の配列を value のみの配列にする
          .map<any>((key: string) => {
            const _key = key as keyof T
            const value = item[_key] as unknown as string
            const _values = item[_key] as unknown as string[]
            let result = ''

            if (_values.length > 1 && typeof _values === 'object') {
              //valueを複数列にする
              _values.forEach((_value: string, index: number) => {
                if (index === 0) {
                  result = `"${_value}"`
                } else {
                  result = result + `,"${_value}"`
                }
              })
              return result
            } else {
              return `"${value}"`
            }
          })
          // value のみの配列を value を `,` で区切る文字列に変更
          .join(',')
      )
    }

    csv += data.map<string>(converObjectToString).join('\n')
  }

  const link = document.createElement('a')
  // \ufeff: BOM を付ける。付けないと Excel で開ける際は文字化けになる。
  link.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent('\ufeff' + csv)
  link.target = LinkOpenTargetEnum.BLANK
  link.download = `${fileName ?? 'new-item'}.csv`
  link.click()
}

/**
 * @description CSV ファイルを文字列に変換
 * @param file CSV ファイル
 * @returns 文字列の CSV
 */
export async function convertCSVFileToString(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const fileReader = new FileReader()
    fileReader.readAsText(file)

    fileReader.onload = (event) =>
      resolve((event.target?.result as string).replace(/["\r]/g, ''))

    fileReader.onerror = (error) => reject(error)
  })
}

/**
 * @description CSV ファイルを配列に変換
 * @param file CSV ファイル
 * @returns 配列の CSV
 */
export async function convertCSVFileToArray(file: File): Promise<string[][]> {
  return new Promise<string[][]>((resolve, reject) => {
    const fileReader = new FileReader()
    fileReader.readAsText(file)

    fileReader.onload = (event) => {
      const rowList: string[] = (event.target?.result as string).split(/\r\n|\n|\r/)

      const cellListedRowList = rowList
        .map<string[]>((row: string) =>
          row
            .split(/,(?=(?:[^\"]*\"[^\"]*\")*(?![^\"]*\"))/)
            .map<string>((cell: string) => cell.replace(/^"|"$/g, ''))
        )
        .filter(
          (item: string[], index: number) =>
            index !== 0 && !item.every((unit: string): boolean => unit === '')
        )

      resolve(cellListedRowList)
    }

    fileReader.onerror = (error) => reject(error)
  })
}

/**
 * @description CSV ファイルを配列に変換 ヘッダ付き
 * @param file CSV ファイル
 * @returns 配列の CSV
 */
export async function convertCSVFileToArrayWithHeader(file: File): Promise<string[][]> {
  return new Promise<string[][]>((resolve, reject) => {
    const fileReader = new FileReader()
    fileReader.readAsText(file)

    fileReader.onload = (event) => {
      const rowList: string[] = (event.target?.result as string).split(/\r\n|\n|\r/)

      const dataList = rowList
        .map<string[]>((row: string) =>
          row
            .split(/,(?=(?:[^\"]*\"[^\"]*\")*(?![^\"]*\"))/)
            .map<string>((cell: string) => cell.replace(/^"|"$/g, ''))
        )

      resolve(dataList)
    }

    fileReader.onerror = (error) => reject(error)
  })
}

/**
 * @description CSV ファイルのヘッダーに空白の列を付け加える
 * @param header ヘッダー
 * @param times 付け加えようとする列の数
 * @returns 空白の列を付け加えたヘッダー
 */
export function appendEmptyCSVColumn(header: string, times: number): string {
  let _header: string = header

  for (let i = 0; i < times; i++) {
    _header += ',""'
  }

  return (_header += '\r\n')
}
